import type { Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
import { TransactionStatus } from '@safe-global/store/gateway/types'
import { useEffect, useMemo, useRef } from 'react'
import { formatError } from '@safe-global/utils/utils/formatters'
import { selectNotifications, showNotification } from '@/store/notificationsSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { TxEvent, txSubscribe } from '@/services/tx/txEvents'
import useChains, { useCurrentChain } from './useChains'
import useTxQueue from './useTxQueue'
import { isSignableBy, isTransactionQueuedItem } from '@/utils/transaction-guards'
import { selectPendingTxs } from '@/store/pendingTxsSlice'
import useIsSafeOwner from '@/hooks/useIsSafeOwner'
import useWallet from './wallets/useWallet'
import useSafeAddress from './useSafeAddress'
import { isWalletRejection } from '@/utils/wallets'
import { getTxLink } from '@/utils/tx-link'
import { useLazyTransactionsGetTransactionByIdV1Query } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { getExplorerLink } from '@safe-global/utils/utils/gateway'
import {
  getGasLimitTooLowMessage,
  getGuardErrorInfo,
  HYPERNATIVE_APPROVAL_REQUIRED_MESSAGE,
  isHypernativeGuardRevert,
  isNonceTooLowError,
  isRateLimitError,
  RATE_LIMIT_USER_MESSAGE,
} from '@/utils/transaction-errors'
import { getGs026Message } from '@safe-global/utils/services/exceptions/contractErrors'
import { getLedgerDeviceError, getLedgerUserMessage } from '@/services/onboard/ledger-errors'
import { getCgwErrorInfo } from '@/utils/cgw-errors'
import { useIsTxFlowOpenRef } from '@/components/tx-flow/useIsTxFlowOpen'
import { useUrlSpaceId } from '@/hooks/useUrlSpaceId'

const TxNotifications = {
  [TxEvent.SIGN_FAILED]: 'Failed to sign. Please try again.',
  [TxEvent.PROPOSED]: 'Successfully added to queue.',
  [TxEvent.PROPOSE_FAILED]: 'Failed to add to queue. Please try again.',
  [TxEvent.DELETED]: 'Successfully deleted transaction.',
  [TxEvent.SIGNATURE_PROPOSED]: 'Successfully signed.',
  [TxEvent.SIGNATURE_PROPOSE_FAILED]: 'Failed to send signature. Please try again.',
  [TxEvent.EXECUTING]: 'Confirm the execution in your wallet.',
  [TxEvent.PROCESSING]: 'Validating...',
  [TxEvent.PROCESSING_MODULE]: 'Validating module interaction...',
  [TxEvent.ONCHAIN_SIGNATURE_REQUESTED]: 'Confirm on-chain signature in your wallet.',
  [TxEvent.ONCHAIN_SIGNATURE_SUCCESS]: 'On-chain signature request confirmed.',
  [TxEvent.PROCESSED]: 'Successfully validated. Indexing...',
  [TxEvent.REVERTED]: 'Reverted. Please check your gas settings.',
  [TxEvent.SUCCESS]: 'Successfully executed.',
  [TxEvent.FAILED]: 'Execution failed.',
}

enum Variant {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error',
}

const successEvents = [TxEvent.PROPOSED, TxEvent.SIGNATURE_PROPOSED, TxEvent.ONCHAIN_SIGNATURE_SUCCESS, TxEvent.SUCCESS]

/** Where the toast points: the transaction page for a queued tx, the explorer for a hash, nowhere without a chain. */
const getNotificationLink = (
  chain: Chain | undefined,
  safeAddress: string,
  txId: string | undefined,
  txHash: string | undefined,
  spaceId: string | null,
): ReturnType<typeof getTxLink> | ReturnType<typeof getExplorerLink> | undefined => {
  if (!chain) return undefined
  if (txId) return getTxLink(txId, chain, safeAddress, spaceId)
  if (txHash) return getExplorerLink(txHash, chain.blockExplorerUriTemplate)
  return undefined
}

const useTxNotifications = (): void => {
  const dispatch = useAppDispatch()
  const chain = useCurrentChain()
  const { configs } = useChains()
  const safeAddress = useSafeAddress()
  const [trigger] = useLazyTransactionsGetTransactionByIdV1Query()
  const isTxFlowOpenRef = useIsTxFlowOpenRef()
  const spaceId = useUrlSpaceId()

  /**
   * Show notifications of a transaction's lifecycle
   */

  useEffect(() => {
    if (!chain) return

    const entries = Object.entries(TxNotifications) as [keyof typeof TxNotifications, string][]

    const unsubFns = entries.map(([event, baseMessage]) =>
      txSubscribe(event, async (detail) => {
        const isError = 'error' in detail
        if (isError && isWalletRejection(detail.error)) return
        // The flow on screen already shows the failure inline, and errors are never listed in the
        // notification center — a toast would only repeat what the flow says.
        if (isError && isTxFlowOpenRef.current) return
        const isSuccess = successEvents.includes(event)

        // A Space-level flow acts on a Safe the page is not on, so the Safe an event names beats the URL's. A chain
        // the app does not know yields no link at all, never the page's chain paired with the event's address.
        const eventSafe =
          'safeAddress' in detail ? { chainId: detail.chainId, safeAddress: detail.safeAddress } : undefined
        const txChainId = eventSafe?.chainId ?? chain.chainId
        const txSafeAddress = eventSafe?.safeAddress ?? safeAddress
        const txChain = eventSafe ? configs.find((config) => config.chainId === eventSafe.chainId) : chain

        // Check if this is a Guard error
        const guardErrorName = isError ? getGuardErrorInfo(detail.error) : undefined
        // Awaiting approval in Hypernative: replaces the guard wording and the raw payload (WA-1219)
        const hnApprovalRequired = isError && isHypernativeGuardRevert(detail.error)
        // A Ledger device failure states its own reason. Its raw error is a
        // dump of DMK class names, ethers codes and the viem version, so it is
        // withheld from `detailedMessage` too (WA-3243).
        const ledgerError = isError ? getLedgerDeviceError(detail.error) : undefined
        // A known CGW response state replaces both the copy and the details:
        // the response body can be a gateway HTML error page (WA-3252).
        const cgwError = isError ? getCgwErrorInfo(detail.error) : undefined
        // A gas limit below the transaction's intrinsic cost: refused pre-broadcast, and fixable
        // by raising the limit, so the toast names the value instead of the raw payload.
        const gasLimitTooLowMessage = isError ? getGasLimitTooLowMessage(detail.error) : undefined

        let message = isError ? `${baseMessage} ${formatError(detail.error)}` : baseMessage

        // Override message for Guard errors
        if (event === TxEvent.REVERTED) {
          // A mined revert means gas was already paid — say so (WA-3005).
          message = `Transaction reverted on ${chain.chainName}. Gas was spent.`
        } else if (hnApprovalRequired) {
          message = HYPERNATIVE_APPROVAL_REQUIRED_MESSAGE
        } else if (guardErrorName) {
          message = `Guard reverted the transaction (${guardErrorName}).`
        } else if (gasLimitTooLowMessage) {
          message = gasLimitTooLowMessage
        } else if (isError && isNonceTooLowError(detail.error)) {
          // The signer wallet's Ethereum nonce advanced before broadcast — the
          // RPC rejected it pre-mining (no gas spent). Same user story as a
          // stale Safe nonce, so show the same message.
          message = getGs026Message('STALE_NONCE')
        } else if (ledgerError) {
          message = getLedgerUserMessage(ledgerError)
        } else if (isError && isRateLimitError(detail.error)) {
          // Translate transient RPC rate-limit failures into friendly copy.
          // The raw error from viem looks like a contract revert ("Request is
          // being rate limited"); we replace the message but keep the original
          // in detailedMessage for debugging.
          // Checked before the CGW classification so a 429-carrying error reads
          // the same here as it does inline in `TxSubmitError` (WA-3252).
          message = RATE_LIMIT_USER_MESSAGE
        } else if (cgwError) {
          message = cgwError.message
        }

        const txId = 'txId' in detail ? detail.txId : undefined
        const txHash = 'txHash' in detail ? detail.txHash : undefined
        const groupKey = 'groupKey' in detail && detail.groupKey ? detail.groupKey : txId || ''

        let humanDescription = 'Transaction'
        const id = txId || txHash
        if (id) {
          try {
            const { data: txDetails } = await trigger({ chainId: txChainId, id })
            humanDescription = txDetails?.txInfo.humanDescription || humanDescription
          } catch {}
        }

        dispatch(
          showNotification({
            title: humanDescription,
            message,
            detailedMessage:
              ledgerError || hnApprovalRequired || cgwError || gasLimitTooLowMessage
                ? undefined
                : isError
                  ? detail.error.message
                  : undefined,
            groupKey,
            variant: isError ? Variant.ERROR : isSuccess ? Variant.SUCCESS : Variant.INFO,
            link: getNotificationLink(txChain, txSafeAddress, txId, txHash, spaceId),
          }),
        )
      }),
    )

    return () => {
      unsubFns.forEach((unsub) => unsub())
    }
  }, [dispatch, safeAddress, chain, configs, trigger, isTxFlowOpenRef, spaceId])

  /**
   * If there's at least one transaction awaiting confirmations, show a notification for it
   */

  const { page } = useTxQueue()
  const isOwner = useIsSafeOwner()
  const pendingTxs = useAppSelector(selectPendingTxs)
  const notifications = useAppSelector(selectNotifications)
  const wallet = useWallet()
  const notifiedAwaitingTxIds = useRef<Array<string>>([])

  const txsAwaitingConfirmation = useMemo(() => {
    if (!page?.results) {
      return []
    }

    return page.results.filter(isTransactionQueuedItem).filter(({ transaction }) => {
      const isAwaitingConfirmations = transaction.txStatus === TransactionStatus.AWAITING_CONFIRMATIONS
      const isPending = !!pendingTxs[transaction.id]
      const canSign = isSignableBy(transaction, wallet?.address || '')
      return isAwaitingConfirmations && !isPending && canSign
    })
  }, [page?.results, pendingTxs, wallet?.address])

  useEffect(() => {
    if (!isOwner || txsAwaitingConfirmation.length === 0) {
      return
    }

    const txId = txsAwaitingConfirmation[0].transaction.id
    const hasNotified = notifiedAwaitingTxIds.current.includes(txId)

    if (hasNotified) {
      return
    }

    dispatch(
      showNotification({
        variant: 'info',
        message: 'A transaction requires your confirmation.',
        link: chain && getTxLink(txId, chain, safeAddress, spaceId),
        groupKey: txId,
      }),
    )

    notifiedAwaitingTxIds.current.push(txId)
  }, [chain, dispatch, isOwner, notifications, safeAddress, txsAwaitingConfirmation, spaceId])
}

export default useTxNotifications
